import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Upload, X, Save, Building2, Milestone, Package } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import MilestoneTemplatesEditor from "../components/MilestoneTemplatesEditor";
import FirmSubscriptionCard from "../components/FirmSubscriptionCard";

const TABS = [
  { key: "profile", label: "Firm profile", icon: Building2 },
  { key: "milestones", label: "Milestone library", icon: Milestone },
  { key: "subscription", label: "Subscription", icon: Package },
];

export default function FirmSettings() {
  const { refresh: refreshAuth } = useAuth();
  const [tab, setTab] = useState("profile");
  const [firm, setFirm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [form, setForm] = useState({
    name: "", uen: "", phone: "", email: "", website: "", address: "",
  });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/firm/settings");
      setFirm(data.firm);
      setForm({
        name: data.firm.name || "",
        uen: data.firm.uen || "",
        phone: data.firm.phone || "",
        email: data.firm.email || "",
        website: data.firm.website || "",
        address: data.firm.address || "",
      });
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const saveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.patch("/firm/settings", form);
      setFirm(data.firm);
      toast.success("Firm profile saved");
      refreshAuth && refreshAuth();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const ok = ["image/png", "image/jpeg", "image/jpg", "image/svg+xml"].includes(file.type);
    if (!ok) { toast.error("Logo must be PNG, JPG or SVG"); return; }
    if (file.size > 2_000_000) { toast.error("Max 2MB"); return; }
    setUploadBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/firm/settings/logo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Logo updated");
      await load();
      refreshAuth && refreshAuth();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploadBusy(false); }
  };

  const removeLogo = async () => {
    try {
      await api.delete("/firm/settings/logo");
      toast.success("Logo removed");
      await load();
      refreshAuth && refreshAuth();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const logoSrc = firm?.logo_url ? `${firm.logo_url}?v=${firm?._logo_cache || Date.now()}` : null;

  return (
    <div className="max-w-4xl mx-auto py-10">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-widest font-bold text-[#7A7571]">Settings</div>
        <h1 className="font-display text-4xl font-bold text-[#2C2A29] mt-2">Firm settings</h1>
        <p className="text-[#7A7571] mt-2">
          Customize your company profile and the default milestone library used for every new project.
        </p>
      </div>

      <div className="flex gap-6 border-b border-[#EBE5D9] mb-8">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.key} onClick={() => setTab(t.key)}
              data-testid={`firm-tab-${t.key}`}
              className={`tab-btn inline-flex items-center gap-2 ${tab === t.key ? "tab-btn-active" : ""}`}
            >
              <Icon size={16} /> {t.label}
            </button>
          );
        })}
      </div>

      {loading && (
        <div className="flex items-center justify-center py-16 text-[#7A7571]">
          <Loader2 size={20} className="animate-spin" />
        </div>
      )}

      {!loading && tab === "profile" && (
        <div className="card-soft" data-testid="firm-profile-card">
          <div className="flex items-start gap-6 mb-6 pb-6 border-b border-[#EBE5D9]">
            <div className="flex-shrink-0">
              <div
                className="w-[200px] h-[200px] rounded-3xl border-2 border-dashed border-[#EBE5D9] bg-[#FBF7EF] flex items-center justify-center overflow-hidden"
                data-testid="firm-logo-preview"
              >
                {logoSrc ? (
                  <img src={logoSrc} alt={form.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="text-center text-[#7A7571]">
                    <Upload size={28} className="mx-auto mb-2" />
                    <div className="text-xs">200 × 200 px</div>
                    <div className="text-[10px]">PNG · JPG · SVG</div>
                  </div>
                )}
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-display text-xl font-bold text-[#2C2A29] mb-2">Company logo</h3>
              <p className="text-sm text-[#7A7571] mb-4">
                Recommended 200 × 200 px. This logo replaces the app title in the top-left for you and everyone in your firm (plus customer &amp; contractor portals).
              </p>
              <div className="flex flex-wrap gap-2">
                <label className="btn-primary inline-flex items-center gap-2 cursor-pointer">
                  {uploadBusy ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                  {uploadBusy ? "Uploading…" : firm?.logo_url ? "Replace logo" : "Upload logo"}
                  <input type="file" accept="image/png,image/jpeg,image/jpg,image/svg+xml"
                    className="hidden" onChange={uploadLogo} data-testid="firm-logo-input" />
                </label>
                {firm?.logo_url && (
                  <button onClick={removeLogo} className="btn-ghost inline-flex items-center gap-2 text-[#9F1239]"
                    data-testid="firm-logo-remove">
                    <X size={16} /> Remove
                  </button>
                )}
              </div>
            </div>
          </div>

          <form onSubmit={saveProfile} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Company name" required>
              <input required className="input-soft" data-testid="firm-name"
                value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </Field>
            <Field label="UEN / business reg #">
              <input className="input-soft" data-testid="firm-uen"
                value={form.uen} onChange={(e) => setForm({ ...form, uen: e.target.value })} />
            </Field>
            <Field label="Phone">
              <input className="input-soft" data-testid="firm-phone"
                value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </Field>
            <Field label="Email">
              <input type="email" className="input-soft" data-testid="firm-email"
                value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </Field>
            <Field label="Website">
              <input className="input-soft" placeholder="https://…" data-testid="firm-website"
                value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} />
            </Field>
            <Field label="Address" fullWidth>
              <input className="input-soft" data-testid="firm-address"
                value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
            </Field>
            <div className="md:col-span-2 flex justify-end">
              <button type="submit" disabled={saving} data-testid="firm-save-btn"
                className="btn-primary inline-flex items-center gap-2">
                {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                {saving ? "Saving…" : "Save profile"}
              </button>
            </div>
          </form>
        </div>
      )}

      {!loading && tab === "milestones" && firm && (
        <MilestoneTemplatesEditor
          title="Firm milestone library"
          subtitle="Every new project you create will start with these milestones (in this order). You can still add, rename or remove milestones inside individual projects afterwards."
          initial={firm.milestone_templates || []}
          saveEndpoint="/firm/milestone-templates"
        />
      )}

      {!loading && tab === "subscription" && (
        <FirmSubscriptionCard />
      )}
    </div>
  );
}

function Field({ label, required, fullWidth, children }) {
  return (
    <div className={fullWidth ? "md:col-span-2" : ""}>
      <label className="text-xs uppercase tracking-widest font-bold text-[#7A7571] mb-1 block">
        {label}{required && <span className="text-[#E07A5F]"> *</span>}
      </label>
      {children}
    </div>
  );
}
